import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Loader2, Play, Trash2, ExternalLink } from 'lucide-react';
import { Badge, BadgeVariant } from '../common/Badge';
import { Button } from '../common/Button';
import { getActiveTenantId } from '../../services/tenantContext';

const OPTIMIZATION_API_URL = import.meta.env.VITE_OPTIMIZATION_API_URL || 'http://localhost:3005';

interface DemoScenario {
  id: string;
  title: string;
  description: string;
  level: 'global' | 'account' | 'region' | 'service';
  type: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  provider: string;
  demoHint: string;
}

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('accessToken');
  return {
    'x-tenant-id': getActiveTenantId(),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const SEVERITY_VARIANT: Record<string, BadgeVariant> = {
  critical: 'danger',
  high: 'warning',
  medium: 'info',
  low: 'muted',
};

const LEVEL_LABEL: Record<string, string> = {
  global: 'Tenant-wide',
  account: 'Account',
  region: 'Region',
  service: 'Service',
};

/**
 * Demo scenario injection.
 *
 * Exists because a sandbox account's spend is normally too flat for any
 * detector to fire, which makes the anomaly → investigate → resolve chain
 * impossible to demonstrate live even though it works. Injecting a finding
 * lets the real UI, real status transitions, and real RCA pipeline all run -
 * only the triggering finding is synthetic.
 *
 * The warning banner and [DEMO] labelling are deliberate and should not be
 * softened: nobody should be able to mistake an injected finding for a real
 * detection, during the demo or months later.
 */
export const DemoScenariosTab: React.FC = () => {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<DemoScenario[]>([]);
  const [injectedCount, setInjectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [clearing, setClearing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastInjectedId, setLastInjectedId] = useState<string | null>(null);

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`${OPTIMIZATION_API_URL}/admin/demo-scenarios`, { headers: authHeaders() });
      setScenarios(res.data?.data?.scenarios || []);
      setInjectedCount(res.data?.data?.injected_count || 0);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Could not load demo scenarios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const inject = async (scenario: DemoScenario) => {
    setBusyId(scenario.id);
    setError(null);
    setMessage(null);
    try {
      const res = await axios.post(
        `${OPTIMIZATION_API_URL}/admin/demo-scenarios/${scenario.id}/inject`,
        {},
        { headers: authHeaders() }
      );
      const anomalyId = res.data?.data?.anomalyId;
      setLastInjectedId(anomalyId || null);
      setMessage(`Created "${scenario.title}". It's now on the Anomalies page.`);
      setInjectedCount((n) => n + 1);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Injection failed');
    } finally {
      setBusyId(null);
    }
  };

  const clearAll = async () => {
    setClearing(true);
    setError(null);
    setMessage(null);
    try {
      const res = await axios.delete(`${OPTIMIZATION_API_URL}/admin/demo-scenarios/injected`, {
        headers: authHeaders(),
      });
      const removed = res.data?.data?.removed ?? 0;
      setMessage(`Removed ${removed} demo ${removed === 1 ? 'finding' : 'findings'}.`);
      setInjectedCount(0);
      setLastInjectedId(null);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Could not clear demo findings');
    } finally {
      setClearing(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/20">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" />
        <div className="text-sm text-amber-800 dark:text-amber-200">
          <span className="font-medium">These create real rows in your anomalies table.</span> Every injected finding is
          prefixed <code className="rounded bg-amber-100 px-1 font-mono text-xs dark:bg-amber-900/40">[DEMO]</code>, is
          excluded from detection-accuracy metrics, and can be removed completely with the button below. Use them to
          demonstrate the anomaly workflow when real cost data is too flat to trigger anything.
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-muted-foreground">
          {injectedCount > 0 ? (
            <>
              <span className="font-medium text-foreground">{injectedCount}</span> demo{' '}
              {injectedCount === 1 ? 'finding' : 'findings'} currently in this tenant
            </>
          ) : (
            'No demo findings currently injected'
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate('/anomalies')}>
            <ExternalLink /> Open Anomalies
          </Button>
          <Button size="sm" variant="danger" onClick={clearAll} disabled={clearing || injectedCount === 0}>
            {clearing ? <Loader2 className="animate-spin" /> : <Trash2 />}
            Remove all demo findings
          </Button>
        </div>
      </div>

      {message && (
        <div className="mb-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-800 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
          {message}
          {lastInjectedId && (
            <button
              onClick={() => navigate(`/anomalies?id=${lastInjectedId}`)}
              className="ml-2 font-medium underline hover:no-underline"
            >
              Open it now
            </button>
          )}
        </div>
      )}
      {error && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm text-rose-800 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading scenarios…
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                  Scenario
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                  Level
                </th>
                <th className="px-4 py-2.5 text-left text-xs font-medium uppercase text-muted-foreground">
                  Severity
                </th>
                <th className="px-4 py-2.5 text-right text-xs font-medium uppercase text-muted-foreground">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {scenarios.map((s) => (
                <tr key={s.id} className="align-top">
                  <td className="px-4 py-3">
                    <div className="font-medium text-foreground">{s.title}</div>
                    <div className="mt-0.5 max-w-xl text-xs text-muted-foreground">{s.description}</div>
                    <div className="mt-1 text-xs italic text-muted-foreground dark:text-muted-foreground">{s.demoHint}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <Badge variant="default">{LEVEL_LABEL[s.level] || s.level}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3">
                    <Badge variant={SEVERITY_VARIANT[s.severity] || 'muted'}>{s.severity.toUpperCase()}</Badge>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right">
                    <Button size="sm" variant="primary" onClick={() => inject(s)} disabled={busyId === s.id}>
                      {busyId === s.id ? <Loader2 className="animate-spin" /> : <Play />}
                      Inject
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
